import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import {
  FiActivity,
  FiAlertTriangle,
  FiCheckCircle,
  FiExternalLink,
  FiPlay,
  FiRefreshCw,
  FiShield,
  FiXCircle,
} from 'react-icons/fi';

import integrationService from '../../services/api/integrationService';
import {
  VIKARCRM_V2_REQUIRED_SCOPES,
  VIKARCRM_V2_REQUIRED_WEBHOOK_EVENTS,
} from '../../pages/admin/integrationCatalog';

const ENABLE_CONFIRMATION = 'ENABLE_VIKARCRM_V2';

const Card = styled.section`
  background: #fff;
  border: 1px solid var(--border-subtle);
  border-radius: 0.5rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  padding: 1.5rem;
  margin-bottom: 1.5rem;
`;
const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1rem;
  flex-wrap: wrap;
`;
const Heading = styled.h2`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 1.15rem;
  color: var(--ve-ink-800);
  margin: 0;
`;
const Description = styled.p`
  color: var(--ve-ink-500);
  font-size: 0.85rem;
  margin: 0.25rem 0 0;
  max-width: 620px;
`;
const BadgeRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.45rem;
  margin-top: 1rem;
`;
const Badge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  border-radius: 999px;
  padding: 0.2rem 0.6rem;
  font-size: 0.75rem;
  font-weight: 700;
  color: ${({ $tone }) => ($tone === 'ok' ? '#176b3a' : $tone === 'bad' ? '#9f2620' : '#765a00')};
  background: ${({ $tone }) => ($tone === 'ok' ? '#eafaf1' : $tone === 'bad' ? '#fdedec' : '#fef9e7')};
`;
const Alert = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.55rem;
  margin-top: 1rem;
  padding: 0.75rem;
  border-radius: 0.35rem;
  border: 1px solid ${({ $error }) => ($error ? '#efb2ae' : '#ecd277')};
  background: ${({ $error }) => ($error ? '#fff3f2' : '#fffbeb')};
  color: ${({ $error }) => ($error ? '#8f231e' : '#725500')};
  font-size: 0.85rem;
  line-height: 1.4;
  svg { flex-shrink: 0; margin-top: 0.15rem; }
`;
const VersionGrid = styled.dl`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.75rem;
  margin: 1rem 0 0;
  @media (max-width: 520px) { grid-template-columns: 1fr; }
`;
const VersionItem = styled.div`
  border: 1px solid var(--border-subtle);
  border-radius: 0.35rem;
  padding: 0.65rem 0.75rem;
  dt { color: var(--ve-ink-500); font-size: 0.72rem; margin-bottom: 0.2rem; }
  dd { color: var(--ve-ink-800); font-size: 0.9rem; font-weight: 700; margin: 0; }
`;
const Section = styled.div`
  margin-top: 1.25rem;
  padding-top: 1.1rem;
  border-top: 1px solid var(--surface-subtle);
`;
const SectionHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  flex-wrap: wrap;
  margin-bottom: 0.65rem;
`;
const SectionTitle = styled.h3`
  font-size: 0.95rem;
  color: var(--ve-ink-800);
  margin: 0;
`;
const SectionMeta = styled.span`
  color: var(--ve-ink-500);
  font-size: 0.75rem;
`;
const CheckList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.5rem;
  @media (max-width: 640px) { grid-template-columns: 1fr; }
`;
const CheckItem = styled.li`
  display: flex;
  align-items: flex-start;
  gap: 0.45rem;
  border: 1px solid var(--border-subtle);
  border-radius: 0.35rem;
  padding: 0.6rem;
  color: var(--ve-ink-700);
  font-size: 0.8rem;
  svg { flex-shrink: 0; margin-top: 0.1rem; color: ${({ $passed }) => ($passed ? '#1e8449' : 'var(--act-danger)')}; }
`;
const CoverageGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.75rem;
  @media (max-width: 680px) { grid-template-columns: 1fr; }
`;
const CoverageBox = styled.div`
  border: 1px solid var(--border-subtle);
  border-radius: 0.35rem;
  padding: 0.75rem;
`;
const CoverageTitle = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 0.5rem;
  color: var(--ve-ink-800);
  font-size: 0.85rem;
  font-weight: 700;
`;
const CoverageDescription = styled.p`
  color: var(--ve-ink-500);
  font-size: 0.75rem;
  margin: 0.25rem 0 0.55rem;
`;
const ChipList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.3rem;
`;
const Chip = styled.code`
  display: inline-block;
  font-size: 0.68rem;
  line-height: 1.3;
  background: var(--surface-subtle);
  color: var(--ve-ink-800);
  border-radius: 0.25rem;
  padding: 0.12rem 0.35rem;
  overflow-wrap: anywhere;
`;
const EntityList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0.65rem 0 0;
`;
const Entity = styled.li`
  border-top: 1px solid var(--surface-subtle);
  padding: 0.5rem 0 0;
  margin-top: 0.5rem;
  color: var(--ve-ink-700);
  font-size: 0.78rem;
`;
const EntityHead = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 0.5rem;
  strong { overflow-wrap: anywhere; }
`;
const Muted = styled.p`
  color: var(--ve-ink-500);
  font-size: 0.78rem;
  margin: 0.5rem 0 0;
`;
const Link = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  color: var(--act-primary);
  font-size: 0.78rem;
  font-weight: 700;
  text-decoration: none;
  &:hover { text-decoration: underline; }
`;
const StatGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0.65rem;
  @media (max-width: 520px) { grid-template-columns: 1fr; }
`;
const Stat = styled.div`
  background: var(--surface-subtle);
  border-radius: 0.35rem;
  padding: 0.65rem;
  color: var(--ve-ink-500);
  font-size: 0.72rem;
  strong { display: block; color: var(--ve-ink-800); font-size: 1.15rem; margin-top: 0.15rem; }
`;
const ActivityGrid = styled.dl`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.65rem;
  margin: 0;
  @media (max-width: 680px) { grid-template-columns: 1fr; }
`;
const ActivityItem = styled.div`
  dt { color: var(--ve-ink-500); font-size: 0.72rem; }
  dd { color: var(--ve-ink-800); font-size: 0.8rem; margin: 0.2rem 0 0; overflow-wrap: anywhere; }
`;
const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.65rem;
  margin-top: 1.25rem;
`;
const Button = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.35rem;
  border: ${({ $variant }) => ($variant === 'secondary' ? '1px solid var(--border-subtle)' : 'none')};
  border-radius: 0.25rem;
  padding: 0.52rem 0.85rem;
  cursor: pointer;
  font-size: 0.82rem;
  font-weight: 700;
  color: ${({ $variant }) => ($variant === 'secondary' ? 'var(--ve-ink-700)' : '#fff')};
  background: ${({ $variant }) => (
    $variant === 'danger' ? 'var(--act-danger)' : $variant === 'secondary' ? '#fff' : 'var(--act-primary)'
  )};
  &:hover:not(:disabled) { filter: brightness(0.96); }
  &:disabled { cursor: not-allowed; opacity: 0.5; }
`;
const Loading = styled.div`
  padding: 1.2rem 0 0;
  color: var(--ve-ink-500);
  font-size: 0.85rem;
`;
const SmokeBox = styled.div`
  margin-top: 0.8rem;
  padding: 0.75rem;
  border: 1px solid #bde5ca;
  border-radius: 0.35rem;
  background: #f3fbf5;
  color: #176b3a;
  font-size: 0.8rem;
`;

const formatDateTime = (value, locale) => {
  if (!value) return '—';
  const parsed = new Date(value);
  return Number.isFinite(parsed.getTime()) ? parsed.toLocaleString(locale) : '—';
};

const safeCode = (value) => String(value || 'unknown')
  .toLowerCase()
  .replace(/[^a-z0-9_]+/g, '_')
  .replace(/^_+|_+$/g, '')
  .slice(0, 80) || 'unknown';

const translatedCode = (t, i18n, prefix, code) => {
  const normalized = safeCode(code);
  const key = `${prefix}.${normalized}`;
  return i18n.exists(key) ? t(key) : normalized.replace(/_/g, ' ');
};

const isPassed = (check) => check?.passed === true || check?.status === 'pass';
const metricCount = (value, available) => {
  if (!available || value === null || value === undefined || value === '') return '—';
  const count = Number(value);
  return Number.isSafeInteger(count) && count >= 0 ? count : '—';
};

const VikarCrmV2Panel = ({ refreshToken = 0 }) => {
  const { t, i18n } = useTranslation();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [smokeResult, setSmokeResult] = useState(null);

  const loadStatus = useCallback(async ({ silent = false } = {}) => {
    try {
      if (!silent) setLoading(true);
      setError(null);
      const result = await integrationService.getVikarCrmV2Status();
      setStatus(result);
      return result;
    } catch (err) {
      setError(err);
      return null;
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadStatus();
  }, [loadStatus, refreshToken]);

  const perform = async (name, action, successKey) => {
    try {
      setBusy(name);
      setError(null);
      setNotice(null);
      const result = await action();
      if (name === 'smoke') setSmokeResult(result);
      else setStatus(result);
      setNotice(t(successKey));
    } catch (err) {
      setError(err);
    } finally {
      setBusy(null);
    }
  };

  const handleEnable = () => {
    if (!status) return;
    if (!window.confirm(t('admin.integrations.vikarcrmV2.actions.confirmEnable'))) return;
    perform(
      'enable',
      () => integrationService.setVikarCrmV2RequestedState({
        enabled: true,
        confirmation: ENABLE_CONFIRMATION,
        stateRevision: status.stateRevision,
      }),
      'admin.integrations.vikarcrmV2.notices.enabled'
    );
  };

  const errorMessage = useMemo(() => {
    if (!error) return null;
    const key = `admin.integrations.vikarcrmV2.errors.${safeCode(error.code)}`;
    const message = i18n.exists(key) ? t(key) : t('admin.integrations.vikarcrmV2.errors.generic');
    return error.requestId
      ? `${message} ${t('admin.integrations.vikarcrmV2.errors.requestId', { requestId: error.requestId })}`
      : message;
  }, [error, i18n, t]);

  if (loading && !status) {
    return (
      <Card aria-labelledby="vikarcrm-v2-title">
        <Heading id="vikarcrm-v2-title"><FiShield /> {t('admin.integrations.vikarcrmV2.title')}</Heading>
        <Loading>{t('common.loading')}</Loading>
      </Card>
    );
  }

  if (!status) {
    return (
      <Card aria-labelledby="vikarcrm-v2-title">
        <Heading id="vikarcrm-v2-title"><FiShield /> {t('admin.integrations.vikarcrmV2.title')}</Heading>
        <Alert $error role="alert"><FiXCircle /> {errorMessage}</Alert>
        <Actions>
          <Button type="button" onClick={() => loadStatus()}><FiRefreshCw /> {t('common.retry')}</Button>
        </Actions>
      </Card>
    );
  }

  const readiness = status.readiness || {};
  const checks = Array.isArray(readiness.checks) ? readiness.checks : [];
  const clients = Array.isArray(readiness.apiClients?.clients) ? readiness.apiClients.clients : [];
  const subscriptions = Array.isArray(readiness.webhooks?.subscriptions) ? readiness.webhooks.subscriptions : [];
  const recentResultCodes = Array.isArray(status.deliveries?.recentResultCodes)
    ? status.deliveries.recentResultCodes
    : [];
  // Missing on an older backend means "not explicitly unavailable". The
  // totals themselves still fail closed to an em dash when absent/null.
  const operationalMetricsAvailable = status.operationalMetricsAvailable !== false;
  const reasonKey = `admin.integrations.vikarcrmV2.reasons.${safeCode(status.reasonCode)}`;
  const reason = i18n.exists(reasonKey) ? t(reasonKey) : t('admin.integrations.vikarcrmV2.reasons.unknown');
  const canEnable = Boolean(
    !status.requestedEnabled && status.platformAllowed && status.readinessPassed && status.stateRevision
  );
  const locale = i18n.resolvedLanguage || i18n.language;

  return (
    <Card aria-labelledby="vikarcrm-v2-title">
      <Header>
        <div>
          <Heading id="vikarcrm-v2-title"><FiShield /> {t('admin.integrations.vikarcrmV2.title')}</Heading>
          <Description>{t('admin.integrations.vikarcrmV2.subtitle')}</Description>
        </div>
        <Button type="button" $variant="secondary" onClick={() => loadStatus()} disabled={Boolean(busy || loading)}>
          <FiRefreshCw /> {t('admin.integrations.vikarcrmV2.actions.refresh')}
        </Button>
      </Header>

      <VersionGrid>
        <VersionItem>
          <dt>{t('admin.integrations.vikarcrmV2.applicationVersion')}</dt>
          <dd>{status.applicationVersion || '—'}</dd>
        </VersionItem>
        <VersionItem>
          <dt>{t('admin.integrations.vikarcrmV2.contractVersion')}</dt>
          <dd>{status.contractVersion || '—'}</dd>
        </VersionItem>
      </VersionGrid>

      <BadgeRow aria-label={t('admin.integrations.vikarcrmV2.stateSummary')}>
        <Badge $tone={status.platformAllowed ? 'ok' : 'bad'}>
          {t('admin.integrations.vikarcrmV2.platform')}: {status.platformAllowed ? t('common.yes') : t('common.no')}
        </Badge>
        <Badge $tone={status.requestedEnabled ? 'ok' : 'warn'}>
          {t('admin.integrations.vikarcrmV2.requested')}: {status.requestedEnabled ? t('admin.integrations.vikarcrmV2.on') : t('admin.integrations.vikarcrmV2.off')}
        </Badge>
        <Badge $tone={status.readinessPassed ? 'ok' : 'bad'}>
          {t('admin.integrations.vikarcrmV2.readiness.label')}: {status.readinessPassed ? t('admin.integrations.vikarcrmV2.ready') : t('admin.integrations.vikarcrmV2.notReady')}
        </Badge>
        <Badge $tone={status.enabled ? 'ok' : 'bad'}>
          {t('admin.integrations.vikarcrmV2.effective')}: {status.enabled ? t('admin.integrations.vikarcrmV2.enabled') : t('admin.integrations.vikarcrmV2.disabled')}
        </Badge>
      </BadgeRow>

      {!status.enabled && (
        <Alert role="alert"><FiAlertTriangle /> <span><strong>{t('admin.integrations.vikarcrmV2.disabledReason')}:</strong> {reason}</span></Alert>
      )}
      {errorMessage && <Alert $error role="alert"><FiXCircle /> {errorMessage}</Alert>}
      {notice && <Alert role="status" aria-live="polite"><FiCheckCircle /> {notice}</Alert>}

      <Section>
        <SectionHead>
          <SectionTitle>{t('admin.integrations.vikarcrmV2.readiness.title')}</SectionTitle>
          <SectionMeta>
            {readiness.checkedAt
              ? t('admin.integrations.vikarcrmV2.readiness.checkedAt', { date: formatDateTime(readiness.checkedAt, locale) })
            : t('admin.integrations.vikarcrmV2.readiness.notChecked')}
          </SectionMeta>
        </SectionHead>
        <Muted>{t('admin.integrations.vikarcrmV2.readiness.nodeScopeNote')}</Muted>
        {checks.length ? (
          <CheckList style={{ marginTop: '0.65rem' }}>
            {checks.map((check) => {
              const passed = isPassed(check);
              return (
                <CheckItem key={check.code} $passed={passed}>
                  {passed ? <FiCheckCircle /> : <FiXCircle />}
                  <span>{translatedCode(t, i18n, 'admin.integrations.vikarcrmV2.checks', check.code)}</span>
                </CheckItem>
              );
            })}
          </CheckList>
        ) : <Muted>{t('admin.integrations.vikarcrmV2.readiness.noResults')}</Muted>}
        {readiness.worker && (
          <ActivityGrid style={{ marginTop: '0.75rem' }}>
            <ActivityItem>
              <dt>{t('admin.integrations.vikarcrmV2.readiness.workerCompleted')}</dt>
              <dd>{formatDateTime(readiness.worker.lastCompletedAt, locale)}</dd>
            </ActivityItem>
            <ActivityItem>
              <dt>{t('admin.integrations.vikarcrmV2.readiness.workerSuccess')}</dt>
              <dd>{formatDateTime(readiness.worker.lastSuccessAt, locale)}</dd>
            </ActivityItem>
            <ActivityItem>
              <dt>{t('admin.integrations.vikarcrmV2.readiness.workerResult')}</dt>
              <dd>{readiness.worker.lastResultCode ? safeCode(readiness.worker.lastResultCode).toUpperCase() : '—'}</dd>
            </ActivityItem>
          </ActivityGrid>
        )}
      </Section>

      <Section>
        <SectionTitle>{t('admin.integrations.vikarcrmV2.coverage.title')}</SectionTitle>
        <CoverageGrid style={{ marginTop: '0.65rem' }}>
          <CoverageBox>
            <CoverageTitle>
              {t('admin.integrations.vikarcrmV2.coverage.apiClients')}
              <Badge $tone={clients.some((client) => client.capable) ? 'ok' : 'bad'}>
                {clients.filter((client) => client.capable).length}
              </Badge>
            </CoverageTitle>
            <CoverageDescription>{t('admin.integrations.vikarcrmV2.coverage.apiClientsHint')}</CoverageDescription>
            <ChipList>{VIKARCRM_V2_REQUIRED_SCOPES.map((scope) => <Chip key={scope}>{scope}</Chip>)}</ChipList>
            {clients.length ? (
              <EntityList>
                {clients.map((client, index) => (
                  <Entity key={`${client.name || 'unnamed'}-${client.createdAt || index}`}>
                    <EntityHead>
                      <strong>{client.name || t('admin.integrations.vikarcrmV2.coverage.unnamed')}</strong>
                      <Badge $tone={client.capable ? 'ok' : 'bad'}>
                        {client.capable
                          ? t('admin.integrations.vikarcrmV2.coverage.complete')
                          : t('admin.integrations.vikarcrmV2.coverage.incomplete')}
                      </Badge>
                    </EntityHead>
                    {!client.capable && Array.isArray(client.missingScopes) && client.missingScopes.length > 0 && (
                      <Muted>{t('admin.integrations.vikarcrmV2.coverage.missing')}: {client.missingScopes.join(', ')}</Muted>
                    )}
                  </Entity>
                ))}
              </EntityList>
            ) : <Muted>{t('admin.integrations.vikarcrmV2.coverage.noClients')}</Muted>}
            <Link href="#api-clients">{t('admin.integrations.vikarcrmV2.coverage.manageApiClients')} <FiExternalLink /></Link>
          </CoverageBox>

          <CoverageBox>
            <CoverageTitle>
              {t('admin.integrations.vikarcrmV2.coverage.webhooks')}
              <Badge $tone={subscriptions.some((subscription) => subscription.complete) ? 'ok' : 'bad'}>
                {subscriptions.filter((subscription) => subscription.complete).length}
              </Badge>
            </CoverageTitle>
            <CoverageDescription>{t('admin.integrations.vikarcrmV2.coverage.webhooksHint')}</CoverageDescription>
            <ChipList>{VIKARCRM_V2_REQUIRED_WEBHOOK_EVENTS.map((event) => <Chip key={event}>{event}</Chip>)}</ChipList>
            {subscriptions.length ? (
              <EntityList>
                {subscriptions.map((subscription, index) => (
                  <Entity key={`${subscription.name || 'unnamed'}-${subscription.createdAt || index}`}>
                    <EntityHead>
                      <strong>{subscription.name || t('admin.integrations.vikarcrmV2.coverage.unnamed')}</strong>
                      <Badge $tone={subscription.complete ? 'ok' : 'bad'}>
                        {subscription.complete
                          ? t('admin.integrations.vikarcrmV2.coverage.complete')
                          : t('admin.integrations.vikarcrmV2.coverage.incomplete')}
                      </Badge>
                    </EntityHead>
                    {!subscription.complete && Array.isArray(subscription.missingEvents) && subscription.missingEvents.length > 0 && (
                      <Muted>{t('admin.integrations.vikarcrmV2.coverage.missing')}: {subscription.missingEvents.join(', ')}</Muted>
                    )}
                  </Entity>
                ))}
              </EntityList>
            ) : <Muted>{t('admin.integrations.vikarcrmV2.coverage.noWebhooks')}</Muted>}
            <Link href="#webhooks">{t('admin.integrations.vikarcrmV2.coverage.manageWebhooks')} <FiExternalLink /></Link>
          </CoverageBox>
        </CoverageGrid>
      </Section>

      <Section role="region" aria-labelledby="vikarcrm-v2-deliveries-title">
        <SectionHead>
          <SectionTitle id="vikarcrm-v2-deliveries-title">{t('admin.integrations.vikarcrmV2.deliveries.title')}</SectionTitle>
          <Link href="#webhook-deliveries">{t('admin.integrations.vikarcrmV2.deliveries.viewRecent')} <FiExternalLink /></Link>
        </SectionHead>
        {!operationalMetricsAvailable && (
          <Alert role="status"><FiAlertTriangle /> {t('admin.integrations.vikarcrmV2.deliveries.metricsUnavailable')}</Alert>
        )}
        <StatGrid>
          <Stat>{t('admin.integrations.vikarcrmV2.deliveries.pending')}<strong>{metricCount(status.deliveries?.pending, operationalMetricsAvailable)}</strong></Stat>
          <Stat>{t('admin.integrations.vikarcrmV2.deliveries.failed')}<strong>{metricCount(status.deliveries?.failed, operationalMetricsAvailable)}</strong></Stat>
          <Stat>{t('admin.integrations.vikarcrmV2.deliveries.deadLettered')}<strong>{metricCount(status.deliveries?.deadLettered, operationalMetricsAvailable)}</strong></Stat>
        </StatGrid>
        <Muted>{t('admin.integrations.vikarcrmV2.deliveries.recentResultCodes')}</Muted>
        {recentResultCodes.length ? (
          <ChipList>
            {recentResultCodes.map(({ code, count }) => <Chip key={code}>{safeCode(code).toUpperCase()}: {Number(count) || 0}</Chip>)}
          </ChipList>
        ) : operationalMetricsAvailable ? (
          <Muted>{t('admin.integrations.vikarcrmV2.deliveries.noResultCodes')}</Muted>
        ) : null}
      </Section>

      <Section>
        <SectionTitle style={{ marginBottom: '0.65rem' }}>{t('admin.integrations.vikarcrmV2.activity.title')}</SectionTitle>
        <ActivityGrid>
          <ActivityItem>
            <dt>{t('admin.integrations.vikarcrmV2.activity.lastRequest')}</dt>
            <dd>{formatDateTime(status.activity?.lastSuccessfulRequestAt, locale)}</dd>
          </ActivityItem>
          <ActivityItem>
            <dt>{t('admin.integrations.vikarcrmV2.activity.lastWebhook')}</dt>
            <dd>{formatDateTime(status.activity?.lastWebhookDeliveryAt, locale)}</dd>
          </ActivityItem>
          <ActivityItem>
            <dt>{t('admin.integrations.vikarcrmV2.activity.lastCursorRequest')}</dt>
            <dd>{formatDateTime(status.activity?.lastChangeCursorRequestAt, locale)}</dd>
          </ActivityItem>
          <ActivityItem>
            <dt>{t('admin.integrations.vikarcrmV2.activity.lastJournalAppend')}</dt>
            <dd>
              {formatDateTime(status.activity?.lastJournalAppendAt, locale)}
              {Number.isInteger(status.activity?.journalAgeSeconds)
                ? ` · ${t('admin.integrations.vikarcrmV2.activity.secondsAgo', { count: status.activity.journalAgeSeconds })}`
                : ''}
            </dd>
          </ActivityItem>
        </ActivityGrid>
      </Section>

      {smokeResult && (
        <SmokeBox role="status" aria-live="polite">
          <strong>{t('admin.integrations.vikarcrmV2.smoke.passed')}</strong>
          {' · '}{t('admin.integrations.vikarcrmV2.smoke.duration', { duration: Number(smokeResult.durationMs) || 0 })}
          {Array.isArray(smokeResult.checks) && (
            <CheckList style={{ marginTop: '0.6rem' }}>
              {smokeResult.checks.map((check) => (
                <CheckItem key={check.code} $passed={isPassed(check)}>
                  {isPassed(check) ? <FiCheckCircle /> : <FiXCircle />}
                  <span>{translatedCode(t, i18n, 'admin.integrations.vikarcrmV2.smokeChecks', check.code)}</span>
                </CheckItem>
              ))}
            </CheckList>
          )}
        </SmokeBox>
      )}

      <Actions aria-label={t('admin.integrations.vikarcrmV2.actions.title')}>
        <Button
          type="button"
          $variant="secondary"
          disabled={Boolean(busy)}
          onClick={() => perform(
            'readiness',
            integrationService.testVikarCrmV2Readiness,
            'admin.integrations.vikarcrmV2.notices.readinessTested'
          )}
        >
          <FiActivity /> {busy === 'readiness'
            ? t('admin.integrations.vikarcrmV2.actions.testingReadiness')
            : t('admin.integrations.vikarcrmV2.actions.testReadiness')}
        </Button>

        {!status.requestedEnabled ? (
          <Button type="button" disabled={Boolean(busy) || !canEnable} onClick={handleEnable}>
            <FiCheckCircle /> {busy === 'enable'
              ? t('admin.integrations.vikarcrmV2.actions.enabling')
              : t('admin.integrations.vikarcrmV2.actions.enable')}
          </Button>
        ) : (
          <Button
            type="button"
            $variant="danger"
            disabled={Boolean(busy)}
            onClick={() => perform(
              'disable',
              () => integrationService.setVikarCrmV2RequestedState({ enabled: false }),
              'admin.integrations.vikarcrmV2.notices.disabled'
            )}
          >
            <FiXCircle /> {busy === 'disable'
              ? t('admin.integrations.vikarcrmV2.actions.disabling')
              : t('admin.integrations.vikarcrmV2.actions.disable')}
          </Button>
        )}

        <Button
          type="button"
          $variant="secondary"
          disabled={Boolean(busy) || !status.enabled}
          onClick={() => perform(
            'smoke',
            integrationService.runVikarCrmV2SmokeTest,
            'admin.integrations.vikarcrmV2.notices.smokePassed'
          )}
        >
          <FiPlay /> {busy === 'smoke'
            ? t('admin.integrations.vikarcrmV2.actions.runningSmoke')
            : t('admin.integrations.vikarcrmV2.actions.runSmoke')}
        </Button>
      </Actions>
    </Card>
  );
};

export default VikarCrmV2Panel;
